import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Plus, MagnifyingGlass, MapPin, CheckCircle, XCircle, Clock, Users,
  UploadSimple, Download, File, Warning, Trash, ArrowsClockwise,
  Funnel, Truck, Package, X, CalendarBlank, Handshake, MapTrifold,
  CaretDown,
} from "@phosphor-icons/react";
import CustomerForm from "./CustomerForm";
import DuplicateReviewModal from "./DuplicateReviewModal";
import { useAuth } from "../../lib/auth";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  active: "bg-[#D4E0D1] text-[#1A3317]",
  approved: "bg-[#D4E0D1] text-[#1A3317]",
  pending_verification: "bg-[#F9E4B7] text-[#7A5A11]",
  pending: "bg-[#F9E4B7] text-[#7A5A11]",
  rejected: "bg-[#F4C7B9] text-[#7A2E18]",
};
const STATUS_ICON = {
  active: CheckCircle, approved: CheckCircle,
  pending_verification: Clock, pending: Clock,
  rejected: XCircle,
};

const EMPTY_FILTERS = {
  registered_from: "", registered_to: "",
  district_id: "", lsgd_id: "", ward_id: "", zone_id: "",
  registration_source: "",
  partner_id: "",
  referred_only: false,
  vehicle_type: "",
  missing_gps: false,
  missing_package: false,
  service_frequency: "",
  service_status: "",
  delayed_days: "",
};

export default function CustomerList({ filterStatus }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [dupReview, setDupReview] = useState(null);  // customer object being reviewed
  const [csvOpen, setCsvOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);  // { mode: 'selected' | 'all' }
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  // Iter 102 — sort + type filter state (server-side)
  const [sortBy, setSortBy] = useState("updated_at");        // updated_at | created_at | name | last_serviced
  const [typeFilter, setTypeFilter] = useState("");          // "" | residential | commercial | ...
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState(null);
  // Iter 139 — advanced filters
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [summary, setSummary] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [lsgds, setLsgds] = useState([]);
  const [wards, setWards] = useState([]);
  const [zones, setZones] = useState([]);
  const [partners, setPartners] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [quickEdit, setQuickEdit] = useState(null); // {mode: "gps"|"package", customer}
  const [packages, setPackages] = useState([]);

  const paramsQs = useMemo(() => {
    const p = new URLSearchParams();
    if (filterStatus) p.set("status", filterStatus);
    if (q) p.set("q", q);
    if (sortBy) p.set("sort_by", sortBy);
    if (typeFilter) p.set("customer_type", typeFilter);
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === false || v == null) return;
      p.set(k, v === true ? "true" : v);
    });
    return p.toString();
  }, [filterStatus, q, sortBy, typeFilter, filters]);

  const load = async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const r = await api.get(`/customers?${paramsQs}`);
      setItems(r.data);
      setLoadMs(Math.round(performance.now() - t0));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const loadSummary = async () => {
    try {
      const p = new URLSearchParams();
      if (filterStatus) p.set("status", filterStatus);
      ["district_id","lsgd_id","ward_id","zone_id"].forEach(k => {
        if (filters[k]) p.set(k, filters[k]);
      });
      const r = await api.get(`/customers/summary?${p.toString()}`);
      setSummary(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, [paramsQs]); // eslint-disable-line
  useEffect(() => { loadSummary(); }, [filterStatus, filters.district_id, filters.lsgd_id, filters.ward_id, filters.zone_id]); // eslint-disable-line

  // Cascading location dropdowns + zones + partners + packages catalogues
  useEffect(() => {
    api.get("/locations?type=district").then(r => setDistricts(r.data || [])).catch(() => {});
    api.get("/zones").then(r => setZones(r.data || [])).catch(() => setZones([]));
    api.get("/partners").then(r => setPartners(r.data || [])).catch(() => setPartners([]));
    api.get("/packages").then(r => setPackages(r.data || [])).catch(() => setPackages([]));
  }, []);
  useEffect(() => {
    if (!filters.district_id) { setLsgds([]); return; }
    api.get(`/locations?type=lsgd&parent_id=${filters.district_id}`)
      .then(r => setLsgds(r.data || [])).catch(() => setLsgds([]));
  }, [filters.district_id]);
  useEffect(() => {
    if (!filters.lsgd_id) { setWards([]); return; }
    api.get(`/locations?type=ward&parent_id=${filters.lsgd_id}`)
      .then(r => setWards(r.data || [])).catch(() => setWards([]));
  }, [filters.lsgd_id]);

  const setF = (patch) => setFilters(f => ({ ...f, ...patch }));
  const activeFilterCount = Object.entries(filters)
    .filter(([, v]) => v !== "" && v !== false && v != null).length;

  const applyRegPreset = (preset) => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    let from = "", to = "";
    if (preset === "today") { from = to = iso(today); }
    else if (preset === "month") {
      from = iso(new Date(today.getFullYear(), today.getMonth(), 1));
      to = iso(today);
    } else if (preset === "lastmonth") {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      from = iso(first); to = iso(last);
    }
    setF({ registered_from: from, registered_to: to });
  };

  const verify = async (id, decision) => {
    try {
      await api.post(`/customers/${id}/verify`, { decision });
      toast.success(`Customer ${decision}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell
      title={filterStatus === "pending_verification" ? "Verification Queue" : "Customers"}
      subtitle="Customer Management"
      actions={
        !filterStatus && (
          <div className="flex gap-2">
            {isSuper && (
              <>
                <button data-testid="btn-delete-selected"
                        disabled={selected.size === 0}
                        onClick={() => setDeleteOpen({ mode: "selected" })}
                        className="inline-flex items-center gap-2 h-10 px-3 border border-[#C25D38] text-[#C25D38] rounded-md text-xs hover:bg-[#F9EDE7] disabled:opacity-40 disabled:cursor-not-allowed">
                  <Trash size={12} weight="bold"/>Delete Selected ({selected.size})
                </button>
              </>
            )}
            <button data-testid="btn-import-customers" onClick={() => setCsvOpen(true)}
                    className="inline-flex items-center gap-2 h-10 px-3 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]">
              <UploadSimple size={12} weight="bold"/>CSV Import
            </button>
            <button data-testid="btn-backfill-zones"
                    onClick={async () => {
                      if (!window.confirm("Sweep all customers and auto-assign Health Zone from ward → zone master mapping?")) return;
                      try {
                        const r = await api.post("/customers/backfill-zones");
                        const d = r.data;
                        let msg = `Fixed ${d.fixed}, cleared ${d.cleared}, no-ward ${d.no_ward}.`;
                        if (d.unmapped_wards_count > 0) {
                          msg += ` ${d.unmapped_wards_count} wards still need mapping (top: ${(d.unmapped_wards_top || []).slice(0,3).map(w => `${w.ward_name} ×${w.customer_count}`).join(", ")}).`;
                        }
                        toast.success(msg, { duration: 8000 });
                        load();
                      } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    className="inline-flex items-center gap-2 h-10 px-3 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]">
              <ArrowsClockwise size={12} weight="bold"/>Auto-fix zones
            </button>
            <button data-testid="btn-new-customer" onClick={() => setOpenForm(true)}
                    className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
              <Plus size={14} weight="bold" /> Add customer
            </button>
          </div>
        )
      }
    >
      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]" />
            <input data-testid="cust-search" value={q} onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                   placeholder="Search name or phone"
                   className="w-full h-10 pl-9 pr-3 border border-[#E5E2D9] rounded-md text-sm" />
          </div>
          <button onClick={load}
                  className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Search</button>
          {/* Iter 102 — sort + type filter */}
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                  data-testid="cust-sort"
                  className="h-10 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
            <option value="updated_at">Sort · Latest edited</option>
            <option value="created_at">Sort · Latest added</option>
            <option value="name">Sort · Name (A–Z)</option>
            <option value="last_serviced">Sort · Latest serviced</option>
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                  data-testid="cust-type-filter"
                  className="h-10 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
            <option value="">All types</option>
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
            <option value="institution">Institution</option>
            <option value="hospital">Hospital</option>
            <option value="hospitality">Hospitality</option>
            <option value="government">Government</option>
          </select>
          {/* Iter 139 — advanced filter drawer toggle */}
          <button data-testid="cust-advanced-toggle"
                  onClick={() => setShowAdvanced(v => !v)}
                  className={`h-10 px-3 rounded-md text-xs inline-flex items-center gap-2 border ${
                    activeFilterCount > 0
                      ? "border-[#2D5A27] bg-[#EFF5EC] text-[#1A3317]"
                      : "border-[#E5E2D9] hover:bg-[#F7F6F2]"
                  }`}>
            <Funnel size={12}/>Filters
            {activeFilterCount > 0 && (
              <span className="bg-[#2D5A27] text-white rounded-full px-1.5 text-[10px]"
                    data-testid="cust-active-count">{activeFilterCount}</span>
            )}
            <CaretDown size={10}/>
          </button>
          {activeFilterCount > 0 && (
            <button data-testid="cust-reset-filters"
                    onClick={() => setFilters(EMPTY_FILTERS)}
                    className="h-10 px-2 text-[11px] text-[#8A8780] hover:text-[#3A3A38]">
              Reset
            </button>
          )}
          <div className="flex-1" />
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]"
               data-testid="cust-list-meta">
            {loading ? "Loading…" : `${items.length} results`}
            {loadMs != null && !loading && (
              <span className="ml-2 text-[10px] normal-case tracking-normal text-[#8A8780]">
                ({loadMs}ms)
              </span>
            )}
          </div>
        </div>

        {/* Iter 139 — advanced filter panel */}
        {showAdvanced && (
          <div className="px-6 py-4 border-b border-[#E5E2D9] bg-[#FAFAF7] space-y-3"
               data-testid="cust-advanced-panel">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] inline-flex items-center gap-1">
                <CalendarBlank size={11}/>Registered
              </span>
              <button data-testid="fltr-reg-today" onClick={() => applyRegPreset("today")}
                      className="h-8 px-2.5 text-[11px] border border-[#E5E2D9] rounded hover:bg-white">Today</button>
              <button data-testid="fltr-reg-month" onClick={() => applyRegPreset("month")}
                      className="h-8 px-2.5 text-[11px] border border-[#E5E2D9] rounded hover:bg-white">This month</button>
              <button data-testid="fltr-reg-lastmonth" onClick={() => applyRegPreset("lastmonth")}
                      className="h-8 px-2.5 text-[11px] border border-[#E5E2D9] rounded hover:bg-white">Last month</button>
              <input type="date" value={filters.registered_from}
                     onChange={e => setF({ registered_from: e.target.value })}
                     data-testid="fltr-reg-from"
                     className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded"/>
              <span className="text-[11px] text-[#8A8780]">→</span>
              <input type="date" value={filters.registered_to}
                     onChange={e => setF({ registered_to: e.target.value })}
                     data-testid="fltr-reg-to"
                     className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded"/>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] inline-flex items-center gap-1">
                <MapTrifold size={11}/>Location
              </span>
              <select value={filters.district_id}
                      onChange={e => setF({ district_id: e.target.value, lsgd_id: "", ward_id: "" })}
                      data-testid="fltr-district"
                      className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded bg-white">
                <option value="">All districts</option>
                {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <select value={filters.lsgd_id} disabled={!filters.district_id}
                      onChange={e => setF({ lsgd_id: e.target.value, ward_id: "" })}
                      data-testid="fltr-lsgd"
                      className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded bg-white disabled:opacity-50">
                <option value="">All LSGDs</option>
                {lsgds.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <select value={filters.ward_id} disabled={!filters.lsgd_id}
                      onChange={e => setF({ ward_id: e.target.value })}
                      data-testid="fltr-ward"
                      className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded bg-white disabled:opacity-50">
                <option value="">All wards</option>
                {wards.map(d => <option key={d.id} value={d.id}>
                  {d.ward_no ? `#${d.ward_no} · ${d.name}` : d.name}
                </option>)}
              </select>
              <select value={filters.zone_id}
                      onChange={e => setF({ zone_id: e.target.value })}
                      data-testid="fltr-zone"
                      className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded bg-white">
                <option value="">All health zones</option>
                {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] inline-flex items-center gap-1">
                <Handshake size={11}/>Source
              </span>
              <select value={filters.registration_source}
                      onChange={e => setF({ registration_source: e.target.value })}
                      data-testid="fltr-regsource"
                      className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded bg-white">
                <option value="">All sources</option>
                <option value="direct">Direct</option>
                <option value="affiliate">Referral registrations</option>
                <option value="partner">Partner referrals</option>
              </select>
              <select value={filters.partner_id}
                      onChange={e => setF({ partner_id: e.target.value })}
                      data-testid="fltr-partner"
                      className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded bg-white">
                <option value="">All partners</option>
                {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <label className="text-[11px] inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={filters.referred_only}
                       onChange={e => setF({ referred_only: e.target.checked })}
                       data-testid="fltr-referred-only"
                       className="accent-[#2D5A27]"/>
                Referred by any customer
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] inline-flex items-center gap-1">
                <Truck size={11}/>Access + service
              </span>
              <select value={filters.vehicle_type}
                      onChange={e => setF({ vehicle_type: e.target.value })}
                      data-testid="fltr-vehicle-type"
                      className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded bg-white">
                <option value="">Any vehicle access</option>
                <option value="2_wheeler">2-wheeler</option>
                <option value="3_wheeler">3-wheeler (auto)</option>
                <option value="4_wheeler">4-wheeler</option>
                <option value="6_wheeler_truck">6-wheeler truck</option>
              </select>
              <select value={filters.service_frequency}
                      onChange={e => setF({ service_frequency: e.target.value })}
                      data-testid="fltr-frequency"
                      className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded bg-white">
                <option value="">Any pickup schedule</option>
                <option value="on_demand">On-demand only</option>
                <option value="weekly">Weekly</option>
                <option value="bi-weekly">Bi-weekly</option>
                <option value="monthly">Monthly</option>
              </select>
              <select value={filters.service_status}
                      onChange={e => setF({ service_status: e.target.value })}
                      data-testid="fltr-service-status"
                      className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded bg-white">
                <option value="">All service statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="temporarily_unavailable">Temporarily unavailable</option>
                <option value="stopped">Stopped</option>
              </select>
              <select value={filters.delayed_days}
                      onChange={e => setF({ delayed_days: e.target.value })}
                      data-testid="fltr-delayed"
                      className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded bg-white">
                <option value="">Any service age</option>
                <option value="7">Delayed 7+ days</option>
                <option value="10">Delayed 10+ days</option>
                <option value="15">Delayed 15+ days</option>
                <option value="30">Delayed 30+ days</option>
                <option value="60">Delayed 60+ days</option>
              </select>
              <label className="text-[11px] inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={filters.missing_gps}
                       onChange={e => setF({ missing_gps: e.target.checked })}
                       data-testid="fltr-missing-gps"
                       className="accent-[#2D5A27]"/>
                Missing GPS
              </label>
              <label className="text-[11px] inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={filters.missing_package}
                       onChange={e => setF({ missing_package: e.target.checked })}
                       data-testid="fltr-missing-package"
                       className="accent-[#2D5A27]"/>
                No package assigned
              </label>
            </div>
          </div>
        )}

        {/* Iter 139 — summary strip */}
        {summary && (
          <div className="px-6 py-3 border-b border-[#E5E2D9] bg-white flex flex-wrap gap-2"
               data-testid="cust-summary-strip">
            <SummaryChip label="Total" value={summary.total} testid="csum-total"/>
            <SummaryChip label="With GPS" value={summary.with_gps} tone="green" testid="csum-with-gps"/>
            <SummaryChip label="Without GPS" value={summary.without_gps} tone="amber" testid="csum-no-gps"/>
            <SummaryChip label="Without package" value={summary.without_package} tone="amber" testid="csum-no-pkg"/>
            <SummaryChip label="Delayed 30d+" value={summary.delayed_30d} tone="red" testid="csum-delayed30"/>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                {isSuper && (
                  <th className="pl-6 pr-2 py-3 w-10">
                    <input type="checkbox" data-testid="cust-select-all"
                           checked={items.length > 0 && items.every(c => selected.has(c.id))}
                           onChange={e => setSelected(e.target.checked ? new Set(items.map(c => c.id)) : new Set())}/>
                  </th>
                )}
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Phone</th>
                <th className="px-6 py-3">GPS</th>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3">Cert / Compliance</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={isSuper ? 8 : 7} className="px-6 py-16 text-center text-[#8A8780]">
                <Users size={28} className="inline-block mb-3 opacity-50" /><br/>No customers yet.
              </td></tr>}
              {items.map((c, i) => {
                const Icon = STATUS_ICON[c.status] || Clock;
                return (
                  <tr key={c.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                    {isSuper && (
                      <td className="pl-6 pr-2 py-3">
                        <input type="checkbox" data-testid={`cust-select-${c.id}`}
                               checked={selected.has(c.id)}
                               onChange={e => {
                                 const s = new Set(selected);
                                 e.target.checked ? s.add(c.id) : s.delete(c.id);
                                 setSelected(s);
                               }}/>
                      </td>
                    )}
                    <td className="px-6 py-3 font-medium">
                      <Link to={`/customers/${c.id}`} data-testid={`cust-name-${c.id}`}
                            className="text-[#2D5A27] hover:underline">{c.name}</Link>
                      {c.customer_code && (
                        <div className="text-[10px] font-mono text-[#8A8780] tracking-wider mt-0.5" data-testid={`cust-code-${c.id}`}>
                          {c.customer_code}
                        </div>
                      )}
                      {/* Iter 77 — waste-type + pickup-schedule chips.
                          Ops can eyeball van planning from the list.  */}
                      {((c.primary_waste_names || []).length > 0
                         || c.auto_schedule_frequency !== "on_demand") && (
                        <div className="mt-1 flex flex-wrap gap-1" data-testid={`cust-chips-${c.id}`}>
                          {(c.primary_waste_names || []).slice(0, 2).map((n) => (
                            <span key={n}
                                  className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F9F0D6] border border-[#F5C518]/50 text-[#7A5A11] font-semibold">
                              {n}
                            </span>
                          ))}
                          {(c.primary_waste_names || []).length > 2 && (
                            <span className="text-[9px] text-[#8A8780]">+{c.primary_waste_names.length - 2}</span>
                          )}
                          {c.auto_schedule_frequency && c.auto_schedule_frequency !== "on_demand" && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#DDE9F6] border border-[#BBDEFB] text-[#0D47A1] font-semibold"
                                  data-testid={`cust-schedule-${c.id}`}>
                              {({
                                weekly: "Weekly pickup",
                                alt_weekly: "Alt-week pickup",
                                monthly: "Monthly pickup",
                              })[c.auto_schedule_frequency] || c.auto_schedule_frequency}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3 font-mono text-xs text-[#5C5A55]">{c.phone}</td>
                    <td className="px-6 py-3 font-mono text-xs text-[#5C5A55]">
                      {c.latitude && c.longitude ? (
                        <span data-testid={`cust-gps-${c.id}`}>
                          <MapPin size={10} className="inline mr-1 text-[#2D5A27]" />
                          {c.latitude?.toFixed(4)}, {c.longitude?.toFixed(4)}
                        </span>
                      ) : (
                        <button onClick={() => setQuickEdit({ mode: "gps", customer: c })}
                                data-testid={`cust-add-gps-${c.id}`}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] bg-[#F4C7B9] text-[#7A2E18] hover:bg-[#EEB09B]">
                          <MapPin size={10}/>Add GPS
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-3 text-xs">
                      <div>{c.customer_type}</div>
                      <div className="text-[10px] text-[#8A8780] mt-0.5">
                        {c.package_name ? (
                          <span data-testid={`cust-package-${c.id}`}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#E4EAF5] text-[#0B2B66] font-semibold">
                            <Package size={9}/>{c.package_name}
                          </span>
                        ) : (
                          <button onClick={() => setQuickEdit({ mode: "package", customer: c })}
                                  data-testid={`cust-add-package-${c.id}`}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#F9E4B7] text-[#7A5A11] hover:bg-[#F4CF82] font-semibold">
                            <Package size={9}/>Assign package
                          </button>
                        )}
                      </div>
                      {c.days_since_service !== undefined && (
                        <div className={`text-[10px] mt-0.5 ${
                          c.days_since_service == null ? "text-[#C25D38]"
                            : c.days_since_service >= 30 ? "text-[#C25D38]"
                            : c.days_since_service >= 15 ? "text-[#7A5A11]"
                            : "text-[#8A8780]"
                        }`} data-testid={`cust-service-age-${c.id}`}>
                          {c.days_since_service == null
                            ? "Never serviced"
                            : `Serviced ${c.days_since_service}d ago`}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3 text-xs">
                      {(() => {
                        const missing = !(c.haritha_mithram_id || "").trim()
                          || !c.property_ownership
                          || c.registration_cert_required === undefined
                          || c.registration_cert_required === null;
                        if (missing) {
                          return (
                            <span data-testid={`cust-cert-${c.id}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] bg-[#F4C7B9] text-[#7A2E18]"
                                  title="Existing customer — please open & fill Haritha Mithram ID, property ownership, and registration certificate status">
                              Need to fill
                            </span>
                          );
                        }
                        if (c.registration_cert_required === true) {
                          return (
                            <span data-testid={`cust-cert-${c.id}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] bg-[#D4E0D1] text-[#1A3317]">
                              Cert required
                            </span>
                          );
                        }
                        return <span className="text-[10px] text-[#8A8780]">—</span>;
                      })()}
                    </td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] ${STATUS_STYLE[c.status] || "bg-[#E5E2D9] text-[#5C5A55]"}`}>
                        <Icon size={10} weight="fill" /> {c.status}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      {(c.status === "pending_verification" || c.status === "pending") && (
                        <>
                          <button data-testid={`approve-${c.id}`} onClick={() => verify(c.id, "active")}
                                  className="text-[#2D5A27] text-xs inline-flex items-center gap-1 mr-3">
                            <CheckCircle size={12} />Approve
                          </button>
                          <button data-testid={`reject-${c.id}`} onClick={() => verify(c.id, "rejected")}
                                  className="text-[#C25D38] text-xs inline-flex items-center gap-1 mr-3">
                            <XCircle size={12} />Reject
                          </button>
                        </>
                      )}
                      {c.duplicate_flag?.soft && (
                        <button data-testid={`dup-flag-${c.id}`}
                                onClick={() => setDupReview(c)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] bg-[#F9E4B7] text-[#7A5A11] hover:bg-[#F4CF82] mr-3 cursor-pointer"
                                title="Click to review potential duplicates">
                          DUP · review
                        </button>
                      )}
                      <Link to={`/customers/${c.id}`} className="text-[#5C5A55] text-xs underline">View</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {openForm && <CustomerForm onClose={() => setOpenForm(false)} onSaved={load} />}
      {dupReview && (
        <DuplicateReviewModal
          customer={dupReview}
          onClose={() => setDupReview(null)}
          onResolved={load}
        />
      )}
      {csvOpen && <CsvImportModal onClose={() => setCsvOpen(false)} onDone={load} />}
      {quickEdit && (
        <QuickEditModal
          mode={quickEdit.mode}
          customer={quickEdit.customer}
          packages={packages}
          onClose={() => setQuickEdit(null)}
          onSaved={() => { setQuickEdit(null); load(); loadSummary(); }}
        />
      )}
      {deleteOpen && (
        <BulkDeleteModal
          mode={deleteOpen.mode}
          count={deleteOpen.mode === "all" ? items.length : selected.size}
          onClose={() => setDeleteOpen(false)}
          onConfirm={async () => {
            const body = { confirm: "DELETE" };
            if (deleteOpen.mode === "all") body.all = true;
            else body.ids = Array.from(selected);
            const r = await api.post("/customers/bulk-delete", body);
            toast.success(`${r.data.deleted} customer(s) deleted`);
            setSelected(new Set()); setDeleteOpen(false); load();
          }}
        />
      )}
    </AppShell>
  );
}

const BulkDeleteModal = ({ mode, count, onClose, onConfirm }) => {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = typed === "DELETE";
  const go = async () => {
    if (!ok) return;
    setBusy(true);
    try { await onConfirm(); } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="bulk-delete-modal">
        <div className="w-12 h-12 rounded-full bg-[#F9EDE7] flex items-center justify-center mb-4">
          <Warning size={22} weight="fill" color="#C25D38"/>
        </div>
        <div className="font-heading text-lg mb-2">
          {mode === "all" ? "Delete ALL customers?" : `Delete ${count} selected customer(s)?`}
        </div>
        <div className="text-sm text-[#5C5A55] mb-4">
          This action is <b>permanent</b> and cannot be undone.
          {mode === "all" && " Every customer in this tenant will be wiped."}
          <br/>Type <code className="font-mono bg-[#F5F2EC] px-1.5 py-0.5 rounded text-[#C25D38]">DELETE</code> to confirm.
        </div>
        <input value={typed} onChange={e => setTyped(e.target.value)} autoFocus
               data-testid="bulk-delete-confirm"
               placeholder="Type DELETE"
               className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm mb-4 font-mono"/>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button onClick={go} disabled={!ok || busy} data-testid="bulk-delete-submit"
                  className="h-10 px-4 bg-[#C25D38] hover:bg-[#A74A29] disabled:opacity-40 text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
            <Trash size={14}/>{busy ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};

const CsvImportModal = ({ onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const download = async () => {
    const token = localStorage.getItem("rescigo_token");
    const r = await fetch(`${API_BASE}/customers/template`,
      { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Template download failed"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "customers_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    if (!file) { toast.error("Pick a CSV file"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/customers/import", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`${r.data.created} imported · ${r.data.skipped} skipped · ${r.data.errors?.length || 0} errors`);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md max-w-lg w-full p-6" onClick={e => e.stopPropagation()} data-testid="csv-import-modal">
        <div className="font-heading text-lg mb-1">Import Customers from CSV</div>
        <div className="text-xs text-[#5C5A55] mb-4">
          Mandatory columns: <code className="font-mono">name</code>,{" "}
          <code className="font-mono">phone</code>.{" "}
          <code className="font-mono">lsgd_name</code>, <code className="font-mono">ward</code>{" "}
          and everything else are optional — leave them blank if you&apos;re
          uploading a name/phone-only list and the office team will fill
          the rest during verification. Imported rows land in the verification queue.
        </div>

        <button onClick={download} data-testid="csv-download-template"
                className="w-full h-10 mb-3 border border-[#2D5A27] text-[#2D5A27] hover:bg-[#EFF3EE] rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
          <Download size={14}/>Download sample template
        </button>

        <label className="block">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">CSV file</div>
          <div className="border-2 border-dashed border-[#E5E2D9] rounded-md p-5 text-center hover:border-[#2D5A27] transition-colors cursor-pointer">
            <input type="file" accept=".csv,text/csv" data-testid="csv-file-input"
                   onChange={e => setFile(e.target.files?.[0] || null)}
                   className="hidden" id="csv-file"/>
            <label htmlFor="csv-file" className="cursor-pointer block">
              <File size={24} className="mx-auto mb-2 text-[#8A8780]"/>
              <div className="text-sm text-[#2D2B26]">{file ? file.name : "Click to pick a .csv file"}</div>
              {file && <div className="text-[11px] text-[#8A8780] mt-1">{(file.size / 1024).toFixed(1)} KB</div>}
            </label>
          </div>
        </label>

        {result && (
          <div className="mt-4 p-3 bg-[#F5F2EC] border border-[#E5E2D9] rounded-md text-xs space-y-1" data-testid="csv-result">
            <div className="flex gap-4">
              <span><CheckCircle size={11} className="inline mr-1 text-[#2D5A27]"/><b>{result.created}</b> created</span>
              <span><Clock size={11} className="inline mr-1 text-[#7A5A11]"/><b>{result.skipped}</b> duplicate/skipped</span>
              <span><Warning size={11} className="inline mr-1 text-[#C25D38]"/><b>{result.errors?.length || 0}</b> errors</span>
            </div>
            {result.errors?.length > 0 && (
              <ul className="mt-2 max-h-32 overflow-y-auto text-[#7A2E18] text-[11px] space-y-0.5">
                {result.errors.slice(0, 20).map((er, i) => (
                  <li key={i}>Row {er.row}: {er.error}</li>
                ))}
                {result.errors.length > 20 && <li>… and {result.errors.length - 20} more</li>}
              </ul>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Close</button>
          <button onClick={upload} disabled={!file || busy} data-testid="csv-upload-submit"
                  className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
            <UploadSimple size={14}/>{busy ? "Uploading…" : "Upload & Import"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Iter 139 — small chip used in the customer summary strip
function SummaryChip({ label, value, tone = "default", testid }) {
  const bg = tone === "green" ? "bg-[#E8F0E6] text-[#1A3317]"
          : tone === "amber" ? "bg-[#F9E4B7] text-[#7A5A11]"
          : tone === "red" ? "bg-[#F4C7B9] text-[#7A2E18]"
          : "bg-[#F7F6F2] text-[#3A3A38]";
  return (
    <div data-testid={testid}
         className={`${bg} border border-[#E5E2D9] rounded-md px-3 py-1.5 min-w-[110px]`}>
      <div className="text-[9px] uppercase tracking-[0.2em] opacity-70">{label}</div>
      <div className="font-heading text-base font-semibold mt-0.5">
        {value?.toLocaleString?.("en-IN") ?? value ?? "—"}
      </div>
    </div>
  );
}

// Iter 139 — inline GPS / package quick-edit modal
function QuickEditModal({ mode, customer, packages, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [lat, setLat] = useState(customer.latitude || "");
  const [lng, setLng] = useState(customer.longitude || "");
  const [packageId, setPackageId] = useState(customer.package_id || "");

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation unavailable"); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(6));
        setLng(pos.coords.longitude.toFixed(6));
        setBusy(false);
      },
      (err) => { toast.error(err.message || "GPS lookup failed"); setBusy(false); },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const save = async () => {
    setBusy(true);
    try {
      if (mode === "gps") {
        if (!lat || !lng) { toast.error("Latitude and longitude required"); setBusy(false); return; }
        await api.patch(`/customers/${customer.id}`, {
          latitude: Number(lat), longitude: Number(lng),
        });
      } else {
        await api.patch(`/customers/${customer.id}`, { package_id: packageId || null });
      }
      toast.success("Customer updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={onClose} data-testid={`quick-edit-${mode}`}>
      <div className="bg-white w-full max-w-md rounded-md p-5 space-y-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Quick edit</div>
            <div className="font-heading text-lg">
              {mode === "gps" ? "Add GPS coordinates" : "Assign package"}
            </div>
            <div className="text-xs text-[#5C5A55] mt-0.5">
              {customer.name} · {customer.phone}
            </div>
          </div>
          <button onClick={onClose} data-testid="quick-edit-close"
                  className="p-1 rounded hover:bg-[#F7F6F2]"><X size={14}/></button>
        </div>

        {mode === "gps" && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-1">Latitude</div>
                <input value={lat} onChange={e => setLat(e.target.value)} inputMode="decimal"
                       data-testid="quick-edit-lat"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono"/>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-1">Longitude</div>
                <input value={lng} onChange={e => setLng(e.target.value)} inputMode="decimal"
                       data-testid="quick-edit-lng"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono"/>
              </div>
            </div>
            <button onClick={useMyLocation} disabled={busy}
                    data-testid="quick-edit-locate"
                    className="w-full h-10 border border-[#2D5A27] text-[#2D5A27] rounded-md text-xs inline-flex items-center justify-center gap-2 hover:bg-[#EFF5EC] disabled:opacity-60">
              <MapPin size={12}/>Use my location
            </button>
          </div>
        )}

        {mode === "package" && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-1">Package</div>
            <select value={packageId} onChange={e => setPackageId(e.target.value)}
                    data-testid="quick-edit-package-select"
                    className="w-full h-11 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
              <option value="">— No package —</option>
              {packages.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.price ? `· ₹${p.price}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        <button onClick={save} disabled={busy} data-testid="quick-edit-save"
                className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium">
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

